import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useApplyTenantBranding } from "@/contexts/branding";
import { TID } from "@/lib/testIds";

export default function TenantLogin() {
  const { slug } = useParams();
  const [branding, setBranding] = useState(null);
  const [legal, setLegal] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [loadingBrand, setLoadingBrand] = useState(true);
  const auth = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/branding/${slug}`);
        setBranding(data.branding);
        setLegal(data.legal_name);
      } catch (e) {
        setErr("Empresa não encontrada ou suspensa.");
      } finally { setLoadingBrand(false); }
    })();
  }, [slug]);

  useApplyTenantBranding(branding);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await auth.login({ email: email.trim().toLowerCase(), password: pw, tenant_slug: slug });
      nav(`/t/${slug}/dashboard`, { replace: true });
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand pane (white-labeled) */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden text-tenant-foreground"
        style={{ background: `hsl(var(--tenant-primary))` }}>
        <div className="absolute inset-0 swiss-grid opacity-[0.08]" />
        <div className="relative flex items-center gap-3">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt="logo" className="w-12 h-12 rounded-lg bg-white/10 object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-white/15 grid place-items-center font-display font-bold text-xl">
              {(legal || "·").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="font-display font-bold text-xl">{branding?.display_name || legal || "Empresa"}</div>
        </div>
        <div className="relative max-w-md space-y-4">
          <div className="text-xs uppercase tracking-[0.25em] opacity-60">Central do Cliente</div>
          <h1 className="font-display text-4xl font-bold leading-tight">
            {branding?.login_headline || "Bem-vindo de volta"}
          </h1>
          <p className="text-base opacity-90 leading-relaxed">
            {branding?.login_subheadline || "Acesse sua central de crescimento."}
          </p>
        </div>
        <Link to="/" className="relative text-xs opacity-60 hover:opacity-100 inline-flex items-center gap-1.5">
          <ArrowLeft className="w-3 h-3" /> voltar
        </Link>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md p-8 rounded-tenant border-border bg-card">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Login da empresa</div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">
            {loadingBrand ? "Carregando..." : (branding?.display_name || legal || slug)}
          </h2>
          <p className="text-sm text-muted-foreground mb-8 mt-1">slug: <span className="font-mono">{slug}</span></p>

          <form onSubmit={submit} className="space-y-5" data-testid={TID.tenantLoginForm}>
            <div className="space-y-2">
              <Label htmlFor="te">Email</Label>
              <Input id="te" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11" data-testid={TID.tenantLoginEmail} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tp">Senha</Label>
              <Input id="tp" type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
                className="h-11" data-testid={TID.tenantLoginPassword} />
            </div>
            {err && <div data-testid={TID.tenantLoginError} className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">{err}</div>}
            <Button type="submit" disabled={busy} data-testid={TID.tenantLoginSubmit}
              className="w-full h-11 gap-2 font-medium btn-tenant border-0 hover:opacity-90"
              style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Entrar
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
